import React, { useState, useEffect } from 'react';
import { useToast } from '../hooks/useToast';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const MATCH_TYPES = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' }
];

export default function ExclusionPatternsModal({ onClose, onSave, onQuery }) {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newPattern, setNewPattern] = useState('');
  const [newMatchType, setNewMatchType] = useState('contains');
  const [newDescription, setNewDescription] = useState('');
  const { success, error: toastError } = useToast();

  const fetchPatterns = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/campaigns/exclusion-patterns`, {
        credentials: 'include'
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to fetch patterns');
      }

      const data = await res.json();
      setPatterns(data);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatterns();
  }, []);

  const handleAddPattern = async (e) => {
    e.preventDefault();

    if (!newPattern.trim()) {
      toastError('Pattern is required');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/campaigns/exclusion-patterns`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pattern: newPattern.trim(),
          match_type: newMatchType,
          description: newDescription.trim() || null
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create pattern');
      }

      success('Pattern created');
      setNewPattern('');
      setNewDescription('');
      fetchPatterns();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (pattern) => {
    try {
      const res = await fetch(`${API_URL}/campaigns/exclusion-patterns/${pattern.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !pattern.is_active })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update pattern');
      }

      fetchPatterns();
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleDelete = async (pattern) => {
    if (!window.confirm(`Delete exclusion pattern "${pattern.pattern}"?`)) return;

    try {
      const res = await fetch(`${API_URL}/campaigns/exclusion-patterns/${pattern.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete pattern');
      }

      success('Pattern deleted');
      fetchPatterns();
    } catch (err) {
      toastError(err.message);
    }
  };

  const getMatchTypeBadge = (matchType) => {
    const styles = {
      contains: 'bg-blue-100 text-blue-800',
      equals: 'bg-green-100 text-green-800',
      starts_with: 'bg-purple-100 text-purple-800',
      ends_with: 'bg-orange-100 text-orange-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[matchType] || styles.contains}`}>
        {MATCH_TYPES.find(m => m.value === matchType)?.label || matchType}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Phone Exclusion Patterns</h2>
            <p className="text-sm text-gray-500 mt-1">
              These patterns filter out invalid or test phone numbers from all campaign recipient lists
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : patterns.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No exclusion patterns configured.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Pattern</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Match Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Description</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Active</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {patterns.map((pattern) => (
                    <tr key={pattern.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-sm font-medium text-gray-900">
                        {pattern.pattern}
                      </td>
                      <td className="px-4 py-3">
                        {getMatchTypeBadge(pattern.match_type)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {pattern.description || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleToggleActive(pattern)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            pattern.is_active ? 'bg-indigo-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              pattern.is_active ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {onQuery && (
                            <button
                              onClick={() => onQuery(pattern)}
                              className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors"
                            >
                              Query
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(pattern)}
                            className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add New Pattern Form */}
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Add New Pattern</h3>
            <form onSubmit={handleAddPattern}>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">Pattern *</label>
                  <input
                    type="text"
                    value={newPattern}
                    onChange={(e) => setNewPattern(e.target.value)}
                    placeholder="e.g. 999, 555-, 000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="w-40">
                  <label className="block text-xs text-gray-600 mb-1">Match Type</label>
                  <select
                    value={newMatchType}
                    onChange={(e) => setNewMatchType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {MATCH_TYPES.map(mt => (
                      <option key={mt.value} value={mt.value}>{mt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="e.g. Test numbers"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving || !newPattern.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
